import { useState } from 'react';

interface Question {
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

const questions: Question[] = [
  {
    question: '¿Qué sistema energético predomina en una serie de 8 repeticiones al 75% 1RM con 90 segundos de descanso?',
    options: [
      'ATP-PC (fosfágenos)',
      'Glucolítico (anaeróbico láctico)',
      'Oxidativo (aeróbico)',
      'Todos por igual'
    ],
    correctAnswer: 1,
    explanation: 'El sistema glucolítico domina en esfuerzos intensos de 10 segundos a 2-3 minutos. Una serie de 8 reps al 75% dura aproximadamente 20-30 segundos, cayendo perfectamente en este rango. El ATP-PC se agota en los primeros 10 segundos, y el oxidativo no tiene tiempo de contribuir significativamente.'
  },
  {
    question: 'Un cliente puede hacer 5 reps al 85% 1RM pero solo 3 reps con el mismo peso después de descansar solo 30 segundos. ¿Qué indica esto?',
    options: [
      'Falta de fuerza muscular',
      'Recuperación insuficiente del sistema ATP-PC',
      'Fatiga central crónica',
      'Mala técnica de ejecución'
    ],
    correctAnswer: 1,
    explanation: 'El sistema ATP-PC se recupera solo 50% en 30 segundos. Para trabajo de fuerza máxima (>85% 1RM) necesitas 3-5 minutos de descanso para recuperar 95% del ATP-PC y mantener la intensidad. Descansos cortos son apropiados para hipertrofia (60-120s) pero no para fuerza máxima.'
  },
  {
    question: '¿Cuál de estas estrategias NO recluta efectivamente fibras musculares tipo II (las que más crecen)?',
    options: [
      'Series de 5 reps al 85% 1RM',
      'Series de 12 reps al 75% 1RM cerca del fallo',
      'Series de 25 reps al 50% 1RM sin llegar al fallo',
      'Series de 6 reps explosivas al 70% 1RM'
    ],
    correctAnswer: 2,
    explanation: 'Las fibras tipo II se reclutan por: 1) alta intensidad (>70-80% 1RM), 2) alta velocidad de contracción, o 3) fatiga acumulada (cerca del fallo). Series de 25 reps al 50% sin llegar al fallo no cumplen ninguno de estos criterios: la carga es demasiado ligera y no hay fatiga suficiente para reclutar fibras de alto umbral.'
  },
  {
    question: 'Un atleta se siente desmotivado, todos los ejercicios se sienten pesados aunque no haya entrenado esos músculos recientemente, y su sueño es irregular. ¿Qué tipo de fatiga presenta?',
    options: [
      'Fatiga periférica (muscular)',
      'Fatiga central (neural)',
      'Depleción de glucógeno',
      'Déficit calórico'
    ],
    correctAnswer: 1,
    explanation: 'Estos son síntomas clásicos de fatiga central: desmotivación, sensación de pesadez generalizada (no específica de un músculo), alteración del sueño. La fatiga periférica sería específica del músculo entrenado. La solución es un deload (reducir volumen/intensidad 40-50% durante 1 semana) y priorizar el sueño, no simplemente comer más o descansar un día.'
  },
  {
    question: '¿Cuál es la jerarquía correcta de los mecanismos de hipertrofia según su importancia?',
    options: [
      'Daño muscular > Tensión mecánica > Estrés metabólico',
      'Estrés metabólico > Daño muscular > Tensión mecánica',
      'Tensión mecánica > Estrés metabólico > Daño muscular',
      'Todos son igualmente importantes'
    ],
    correctAnswer: 2,
    explanation: 'La tensión mecánica es el estímulo primario para la hipertrofia (cargas 70-85%, control excéntrico, ROM completo, cerca del fallo). El estrés metabólico es secundario y complementario (pump, 12-20 reps, descansos cortos). El daño muscular es terciario: contribuye pero no es necesario, y demasiado daño (DOMS extremo) interfiere con el volumen de entrenamiento. No busques DOMS activamente.'
  }
];

export default function SelfAssessment() {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [score, setScore] = useState(0);
  const [completed, setCompleted] = useState(false);

  const handleAnswer = (answerIndex: number) => {
    if (showExplanation) return;
    
    setSelectedAnswer(answerIndex);
    setShowExplanation(true);
    
    if (answerIndex === questions[currentQuestion].correctAnswer) {
      setScore(score + 1);
    }
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
    } else {
      setCompleted(true);
    }
  };

  const handleRestart = () => {
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setShowExplanation(false);
    setScore(0);
    setCompleted(false);
  };

  if (completed) {
    const percentage = Math.round((score / questions.length) * 100);
    let feedback = '';
    let feedbackColor = '';

    if (percentage >= 80) {
      feedback = '¡Excelente! Dominas los conceptos de fisiología del ejercicio. Estás listo para aplicarlos en el diseño de programas.';
      feedbackColor = 'text-green-700';
    } else if (percentage >= 60) {
      feedback = 'Buen trabajo. Comprendes los conceptos principales, pero revisa las áreas donde fallaste para fortalecer tu conocimiento.';
      feedbackColor = 'text-yellow-700';
    } else {
      feedback = 'Necesitas repasar el módulo. Los conceptos de fisiología son fundamentales para diseñar entrenamientos efectivos. Revisa las lecciones y vuelve a intentarlo.';
      feedbackColor = 'text-red-700';
    }

    return (
      <div className="space-y-8">
        <div className="bg-gradient-to-br from-[#1A3A2E] to-[#0D1F17] rounded-2xl p-8 text-white text-center">
          <i className="ri-trophy-line text-6xl text-[#39FF14] mb-4"></i>
          <h2 className="text-3xl font-bold mb-4">Autoevaluación Completada</h2>
          <div className="text-5xl font-bold text-[#39FF14] mb-2">{score}/{questions.length}</div>
          <p className="text-xl text-gray-200">Respuestas correctas ({percentage}%)</p>
        </div>

        <div className="bg-white rounded-2xl p-8 border border-gray-200">
          <h3 className="text-2xl font-bold text-black mb-4">Retroalimentación</h3>
          <p className={`text-lg leading-relaxed ${feedbackColor}`}>{feedback}</p>
        </div>

        <button
          onClick={handleRestart}
          className="w-full px-8 py-4 bg-[#39FF14] text-black font-bold rounded-xl hover:bg-[#2DE00F] transition-all whitespace-nowrap cursor-pointer"
        >
          Reintentar Evaluación
        </button>
      </div>
    );
  }

  const question = questions[currentQuestion];

  return (
    <div className="space-y-8">
      <div className="bg-gradient-to-br from-[#1A3A2E] to-[#0D1F17] rounded-2xl p-8 text-white">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <i className="ri-question-line text-3xl text-[#39FF14]"></i>
            <h2 className="text-3xl font-bold">Autoevaluación</h2>
          </div>
          <div className="text-sm font-medium bg-white/10 px-4 py-2 rounded-full backdrop-blur-sm">
            Pregunta {currentQuestion + 1} de {questions.length}
          </div>
        </div>
        <div className="w-full bg-white/20 rounded-full h-2 mb-4">
          <div 
            className="bg-[#39FF14] h-2 rounded-full transition-all duration-300"
            style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
          ></div>
        </div>
      </div>

      <div className="bg-white rounded-2xl p-8 border border-gray-200">
        <h3 className="text-xl font-bold text-black mb-6 leading-relaxed">{question.question}</h3>
        <div className="space-y-3">
          {question.options.map((option, index) => {
            let buttonClass = 'w-full text-left px-6 py-4 rounded-xl border-2 transition-all whitespace-nowrap cursor-pointer ';
            
            if (showExplanation) {
              if (index === question.correctAnswer) {
                buttonClass += 'border-green-500 bg-green-50 text-green-900';
              } else if (index === selectedAnswer) {
                buttonClass += 'border-red-500 bg-red-50 text-red-900';
              } else {
                buttonClass += 'border-gray-200 bg-gray-50 text-gray-500';
              }
            } else {
              buttonClass += selectedAnswer === index
                ? 'border-[#39FF14] bg-[#39FF14]/10 text-black'
                : 'border-gray-200 bg-white text-gray-700 hover:border-[#39FF14]';
            }

            return (
              <button
                key={index}
                onClick={() => handleAnswer(index)}
                disabled={showExplanation}
                className={buttonClass}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full border-2 flex items-center justify-center flex-shrink-0">
                    {showExplanation && index === question.correctAnswer && (
                      <i className="ri-check-line text-lg"></i>
                    )}
                    {showExplanation && index === selectedAnswer && index !== question.correctAnswer && (
                      <i className="ri-close-line text-lg"></i>
                    )}
                    {!showExplanation && <span className="text-sm font-bold">{String.fromCharCode(65 + index)}</span>}
                  </div>
                  <span className="text-base leading-relaxed">{option}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {showExplanation && (
        <div className="bg-[#F5F2EB] rounded-2xl p-8 border-l-4 border-[#39FF14]">
          <h4 className="font-bold text-black mb-3 flex items-center gap-2">
            <i className="ri-information-line text-xl text-[#1A3A2E]"></i>
            Explicación
          </h4>
          <p className="text-gray-700 leading-relaxed mb-6">{question.explanation}</p>
          <button
            onClick={handleNext}
            className="px-8 py-3 bg-[#39FF14] text-black font-bold rounded-xl hover:bg-[#2DE00F] transition-all whitespace-nowrap cursor-pointer"
          >
            {currentQuestion < questions.length - 1 ? 'Siguiente Pregunta' : 'Ver Resultados'}
          </button>
        </div>
      )}
    </div>
  );
}